// src/services/axiosClient.js
import axios from "axios";
import { VITE_API_BASE_URL } from "../config";
import { LOGIN_PATH } from "../router/routes";

const ACCESS_KEY = "ACCESS_TOKEN";
const REFRESH_PATH = "/refresh-token"; // backend route that rotates access token

const axiosClient = axios.create({
  baseURL: `${VITE_API_BASE_URL}/api`,
  withCredentials: true, // <- send/receive cookies
});

let isRefreshing = false;
let refreshSubscribers = [];
let refreshPromise = null;

function subscribeTokenRefresh(cb) {
  refreshSubscribers.push(cb);
}
function onTokenRefreshed(newToken) {
  refreshSubscribers.forEach((cb) => cb(null, newToken));
  refreshSubscribers = [];
}
function onTokenRefreshFailed(err) {
  refreshSubscribers.forEach((cb) => cb(err, null));
  refreshSubscribers = [];
}

axiosClient.interceptors.request.use((config) => {
  // Skip auth header on refresh call only
  const skipAuth =
    config.__skipAuth === true || config.url?.includes(REFRESH_PATH);

  if (!skipAuth) {
    const token = localStorage.getItem(ACCESS_KEY);
    if (token) config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

axiosClient.interceptors.response.use(
  (r) => r,
  async (error) => {
    const original = error.config;

    // Network / CORS issues
    if (!error.response) return Promise.reject(error);

    const { status } = error.response;

    // Only handle 401 here
    if (status !== 401) return Promise.reject(error);

    // Avoid loops
    if (original._retry) return Promise.reject(error);

    // If the refresh call itself 401s, force logout
    const isRefreshCall =
      original?.url?.includes(REFRESH_PATH) || original?.__skipAuth === true;
    if (isRefreshCall) {
      localStorage.removeItem(ACCESS_KEY);
      if (window.location.pathname !== LOGIN_PATH)
        window.location.replace(LOGIN_PATH);
      return Promise.reject(error);
    }

    // Queue while refresh in-flight
    if (isRefreshing && refreshPromise) {
      original._retry = true;
      return new Promise((resolve, reject) => {
        subscribeTokenRefresh((err, newToken) => {
          if (err) return reject(err);
          original.headers.Authorization = `Bearer ${newToken}`;
          resolve(axiosClient(original));
        });
      });
    }

    // Start refresh (use bare axios to avoid interceptors)
    isRefreshing = true;
    original._retry = true;

    refreshPromise = axios.post(
      `${VITE_API_BASE_URL}/api${REFRESH_PATH}`,
      {}, // no body needed; cookie carries refresh token
      { withCredentials: true, headers: {}, __skipAuth: true }
    );

    try {
      const resp = await refreshPromise;
      const newToken = resp?.data?.access_token;
      if (!newToken) throw new Error("No access_token in refresh response");

      // Save / wake queued
      localStorage.setItem(ACCESS_KEY, newToken);
      onTokenRefreshed(newToken);

      // Retry the original request
      original.headers.Authorization = `Bearer ${newToken}`;
      return axiosClient(original);
    } catch (e) {
      onTokenRefreshFailed(e);
      localStorage.removeItem(ACCESS_KEY);
      if (window.location.pathname !== LOGIN_PATH)
        window.location.replace(LOGIN_PATH);
      return Promise.reject(e);
    } finally {
      isRefreshing = false;
      refreshPromise = null;
    }
  }
);

export default axiosClient;
